import { Controller } from "@hotwired/stimulus";
import { parseNumber, formatNumberArgentina } from "../utils/number_formatter";

export default class extends Controller {
  static targets = [
    "quantity",
    "price",
    "totalPrice",
    "totalPricePo",
    "hiddenTotalPrice",
    "selectedSku",
    "warningMessage",
    "field",
    "ivaIncluded",
    "ivaRate",
    "subtotalDisplay",
    "ivaDisplay",
    "discountPercent",
    "discountDisplay",
    "perceptionCabaPercent",
    "perceptionBsasPercent",
    "perceptionIvaPercent",
    "perceptionsDisplay",
    "excludeDiscount",
    "excludeDiscountWrapper",
  ];

  // Use shared utility functions
  parseNumber = parseNumber
  formatNumberArgentina = formatNumberArgentina

  connect() {
    // Formatear valores iniciales si existen
    this.formatInitialValues();
    this.calculateTotalPrice();
    this.checkFormValidity();
  }

  // Formatea los valores iniciales de los campos al cargar el formulario
  formatInitialValues() {
    // Formatear campos de precio
    this.priceTargets.forEach((priceTarget) => {
      if (priceTarget.value && priceTarget.value !== "") {
        const parsed = this.parseNumber(priceTarget.value);
        if (parsed > 0) {
          priceTarget.value = this.formatNumberArgentina(parsed, 2);
        }
      }
    });

    // Formatear campos de total
    this.totalPriceTargets.forEach((totalPriceTarget) => {
      if (totalPriceTarget.value && totalPriceTarget.value !== "" && totalPriceTarget.value !== "0.0" && totalPriceTarget.value !== "0,0") {
        const parsed = this.parseNumber(totalPriceTarget.value);
        totalPriceTarget.value = this.formatNumberArgentina(parsed, 2);
      } else {
        totalPriceTarget.value = "0,00";
      }
    });

    // Formatear total general
    if (this.totalPricePoTarget && this.totalPricePoTarget.value && this.totalPricePoTarget.value !== "0.00" && this.totalPricePoTarget.value !== "0,00") {
      const parsed = this.parseNumber(this.totalPricePoTarget.value);
      this.totalPricePoTarget.value = this.formatNumberArgentina(parsed, 2);
    } else if (this.totalPricePoTarget) {
      this.totalPricePoTarget.value = "0,00";
    }
  }

  // Formatea el campo de precio cuando el usuario sale del campo (blur)
  formatPriceInput(event) {
    const input = event.target;
    if (input.value && input.value !== "") {
      const parsed = this.parseNumber(input.value);
      // Guardar el valor parseado en formato estándar en un atributo data
      input.dataset.standardValue = parsed.toFixed(2);
      // Mostrar el valor formateado al usuario
      input.value = this.formatNumberArgentina(parsed, 2);
      // Recalcular totales después de formatear
      this.calculateTotalPrice();
    }
  }

  calculateTotalPrice() {
    let quantityTargets = this.quantityTargets;
    let priceTargets = this.priceTargets;
    let totalPriceTargets = this.totalPriceTargets;

    const ivaIncluded = this.hasIvaIncludedTarget && this.ivaIncludedTarget.checked;

    // Descuento global de la OC (DTO del proveedor, aplicado antes del IVA)
    let discountPercent = 0;
    if (this.hasDiscountPercentTarget) {
      discountPercent = this.parseNumber(this.discountPercentTarget.value);
      if (isNaN(discountPercent) || discountPercent < 0) discountPercent = 0;
      if (discountPercent >= 100) discountPercent = 0;
    }
    const discountFactor = 1 - discountPercent / 100;

    let sumSubtotal = 0;
    let sumIva = 0;
    let sumDiscount = 0;

    totalPriceTargets.forEach((totalPriceTarget, index) => {
      // Cantidad es un <input type="number">: el navegador siempre expone el
      // valor canónico con punto decimal ("4.241" = 4,241). NO pasa por
      // parseNumber, que lo leería como miles (4.241 → 4241) e inflaba el
      // total ×1000.
      let quantity = parseFloat(quantityTargets[index].value);
      let price = this.parseNumber(priceTargets[index].value);

      // Validate parsed values
      if (isNaN(quantity)) quantity = 0;
      if (isNaN(price)) price = 0;

      // Ítems marcados "sin descuento" quedan afuera del DTO global de la OC.
      const excludeDiscount = this.excludeDiscountTargets[index]?.checked;
      const lineDiscountFactor = excludeDiscount ? 1 : discountFactor;

      let grossLineTotal = quantity * price;
      let lineTotal = grossLineTotal * lineDiscountFactor;
      sumDiscount += grossLineTotal - lineTotal;

      // Get IVA rate for this line
      let ivaRate = 21.0;
      if (this.hasIvaRateTarget && this.ivaRateTargets[index]) {
        ivaRate = parseFloat(this.ivaRateTargets[index].value) || 21.0;
      }
      let rate = ivaRate / 100.0;

      let lineIva = 0;
      let lineNet = 0;

      if (ivaIncluded) {
        // Prices include IVA - extract it
        lineNet = lineTotal / (1 + rate);
        lineIva = lineTotal - lineNet;
      } else {
        // Prices don't include IVA - add it
        lineNet = lineTotal;
        lineIva = lineTotal * rate;
      }

      sumSubtotal += lineNet;
      sumIva += lineIva;

      // Display line total (price * quantity, as entered — sin descuento,
      // para que la columna coincida con el renglón de la factura). El title
      // muestra el valor completo si el monto desborda el ancho del campo.
      totalPriceTarget.value = this.formatNumberArgentina(grossLineTotal, 2);
      totalPriceTarget.title = totalPriceTarget.value;
    });

    // Percepciones IIBB (CABA + BsAs): % sobre el subtotal neto
    let perceptionPercent = 0;
    [this.hasPerceptionCabaPercentTarget && this.perceptionCabaPercentTarget,
     this.hasPerceptionBsasPercentTarget && this.perceptionBsasPercentTarget,
     this.hasPerceptionIvaPercentTarget && this.perceptionIvaPercentTarget].forEach((target) => {
      if (!target) return;
      let value = this.parseNumber(target.value);
      if (isNaN(value) || value < 0 || value >= 100) value = 0;
      perceptionPercent += value;
    });
    const sumPerceptions = sumSubtotal * (perceptionPercent / 100);

    // Calculate grand total
    let grandTotal = sumSubtotal + sumIva + sumPerceptions;

    // Update discount display
    if (this.hasDiscountDisplayTarget) {
      this.discountDisplayTarget.textContent = sumDiscount > 0
        ? `-${this.formatNumberArgentina(sumDiscount, 2)}`
        : "0,00";
    }

    // Update subtotal display
    if (this.hasSubtotalDisplayTarget) {
      this.subtotalDisplayTarget.textContent = this.formatNumberArgentina(sumSubtotal, 2);
    }

    // Update IVA display
    if (this.hasIvaDisplayTarget) {
      this.ivaDisplayTarget.textContent = this.formatNumberArgentina(sumIva, 2);
    }

    // Update perceptions display
    if (this.hasPerceptionsDisplayTarget) {
      this.perceptionsDisplayTarget.textContent = this.formatNumberArgentina(sumPerceptions, 2);
    }

    // Update total
    this.totalPricePoTarget.value = this.formatNumberArgentina(grandTotal, 2);

    // El checkbox "Sin descuento" por ítem solo tiene sentido con DTO global > 0
    this.excludeDiscountWrapperTargets.forEach((wrapper) => {
      wrapper.classList.toggle("d-none", discountPercent <= 0);
    });

    this.checkFormValidity()
  }

  recalculateTotalPricePo(){
    this.calculateTotalPrice()
  }

  checkFormValidity() {
    const emptyQuantity = this.quantityTargets.some((input) => input.value === "");
    const emptyPrice = this.priceTargets.some((input) => input.value === "");
    const emptySkus = this.selectedSkuTargets.some((input) => input.value === "");

    const selectedSkus = this.selectedSkuTargets.map((input) => input.value);
    const duplicateSkus = selectedSkus.filter((sku, index) => selectedSkus.indexOf(sku) !== index);

    if (emptyQuantity || emptyPrice || emptySkus || duplicateSkus.length > 0) {
      $(':input[id="po-submit"]').prop("disabled", true);
      this.warningMessageTarget.classList.remove("d-none");
    } else {
      $(':input[id="po-submit"]').prop("disabled", false);
      this.warningMessageTarget.classList.add("d-none");
    }
  }

  removeField(event) {
    const willRemove = event.currentTarget.dataset["removable"] === "true"

    // Encontrar el campo padre que contiene el botón clickeado
    const fieldElement = event.currentTarget.closest('[data-purchase-order-target="field"]')

    if (willRemove && fieldElement) {
      // Si el campo tiene un ID (es un registro existente), marcar como _destroy
      const destroyInput = fieldElement.querySelector('input[name$="[_destroy]"]');
      if (destroyInput) {
        destroyInput.value = '1';
      }

      // Remover el elemento del DOM
      fieldElement.remove();
    } else if (fieldElement) {
      fieldElement.classList.add("d-none");
    }
    this.calculateTotalPrice()
  }
}
